import { HubCoreService, MySquid, onConfigChange, onUserChange, User } from '@hub/shared'
import { menuItem } from '@layouts/default/default.layout'
import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TFunction, useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'
import './mobile-navigation-default.component.scoped.scss'
import { useFetchAllProfiles } from '@src/hooks/useFetchAllProfiles'
import { useCreatorsInsights } from '@src/hooks/useCreatorsInsights'
import { SqModalActivation, SqModalWelcomeCreatorsInsights } from '@squidit/react-css'
import { useDeviceType } from '@src/hooks/useDeviceType'
import { showInsights } from '@src/utils/showInsights'

export const MenuItemRender = ({ item, t }: { item: menuItem; t: TFunction<'defaultLayout', undefined> }) => {
  const [active, setActive] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const isMobile = useDeviceType()

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  const hoverObj = isMobile
    ? {}
    : {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }

  const handleClick = (item: menuItem, e: any) => {
    e.preventDefault()

    if (item.onClick && typeof item.onClick === 'function') {
      item.onClick()
    }
  }

  const Icon = () => (
    <>
      <div className={`${active || isHovered ? 'active' : ''} icon-wrapper`}>
        {item.icon ? <i className={`${active || isHovered ? 'fa-solid active' : 'fa-regular'} fa-${item.icon} icon-mobile`} /> : null}
      </div>
      <span className={`${active || isHovered ? 'active' : ''} label`}>{t(item.label)}</span>
    </>
  )

  if (item.href) {
    return (
      <li {...hoverObj}>
        <a href={item.href} target={item.target}>
          <Icon />
        </a>
      </li>
    )
  }
  if (item.path) {
    return (
      <li {...hoverObj}>
        {' '}
        <NavLink
          end
          className={({ isActive }) => {
            setActive(isActive)
            return ''
          }}
          title={t(item.label)}
          to={item.path}
        >
          <Icon />
        </NavLink>
      </li>
    )
  }
  if (item.onClick && typeof item.onClick === 'function') {
    return (
      <li {...hoverObj}>
        {' '}
        <button
          type="button"
          onClick={(e) => {
            handleClick(item, e)
          }}
          title={t(item.label)}
        >
          <Icon />
        </button>
      </li>
    )
  }

  return <></>
}

interface MobileNavigationDefaultProps {
  className: string
  configs?: MySquid | null
}

export const MobileNavigationDefault = ({ className, configs }: MobileNavigationDefaultProps) => {
  const onUserChangeSubscription = useRef<SubscriptionLike>()
  const [user, setUser] = useState<User | null>(null)
  const { t } = useTranslation('defaultLayout')
  const { profiles, setProfiles, fetchAllProfiles, profilesLoading } = useFetchAllProfiles(user?.squidId)
  const [registerCompleted, setRegisterCompleted] = useState<boolean>(false)
  const hubCoreService = useMemo(() => new HubCoreService(), [])

  const {
    isModalOpen,
    setIsModalOpen,
    handleButtonCreatorsInsightsClick,
    handleConfirm,
    isActivationModalOpen,
    setIsActivationModalOpen,
    handleToggleActivation,
    handleTogglePublicProfile,
    loadingToken,
  } = useCreatorsInsights({
    user,
    setProfiles,
  })

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: any) => {
      setUser(user)
      if (!user?.squidId) {
        setRegisterCompleted(false)
        return
      }
      setRegisterCompleted((await hubCoreService.getRegisterCompleteness(user?.squidId))?.completed)
    })

    return () => {
      onUserChangeSubscription.current?.unsubscribe()
    }
  }, [onUserChangeSubscription])

  const handleCreatorsInsightsClick = async () => {
    const allProfiles = (await fetchAllProfiles()) ?? []

    handleButtonCreatorsInsightsClick(allProfiles)
  }

  const mobileMenu: menuItem[] = [
    {
      label: 'userMenu.home',
      path: '/home',
      icon: 'house',
    },
    {
      label: 'userMenu.campaigns',
      path: '/campaigns',
      icon: 'bullhorn',
    },
    {
      label: 'mainMenu.pay',
      path: '/payments/info',
      icon: 'circle-dollar',
    },
  ]

  if (showInsights({ configs, user })) {
    mobileMenu.splice(1, 0, {
      label: 'userMenu.insights',
      icon: 'chart-user',
      onClick: handleCreatorsInsightsClick,
    })
  }

  const getStylePosition = useCallback((): CSSProperties => {
    if (navigator.userAgent.match(/(iPod|iPhone|iPad)/)) {
      return { position: 'fixed' }
    }
    return {}
  }, [])

  return (
    <>
      {isModalOpen && (
        <SqModalWelcomeCreatorsInsights
          open={isModalOpen}
          modalSize="xl"
          buttonClose={true}
          onCloseChange={() => setIsModalOpen(false)}
          onOpenChange={(open: boolean) => setIsModalOpen(open)}
          forceMobileNoMargin={true}
          onConfirm={() => handleConfirm(profiles)}
          loading={loadingToken}
        />
      )}
      {isActivationModalOpen && (
        <SqModalActivation
          open={isActivationModalOpen}
          requireActiveProfile
          onConfirm={() => handleButtonCreatorsInsightsClick(profiles)}
          onToggleCreatorsInsights={handleToggleActivation}
          onTogglePublicProfile={handleTogglePublicProfile}
          loading={profilesLoading}
          profiles={
            profiles?.map((profile) => ({
              profileId: profile.profileId,
              socialNetwork: profile.socialNetwork,
              username: profile.username,
              hasCreatorsInsights: profile.hasCreatorsInsights ?? false,
              picture: profile.picture,
              followers: profile.followers,
              hasSocialNetworkCache: profile.hasSocialNetworkCache,
              isSharedCreatorsInsights: profile.isSharedCreatorsInsights ?? false,
              hasValidToken: profile.hasValidToken,
            })) ?? []
          }
          onOpenChange={(open: boolean) => setIsActivationModalOpen(open)}
          titleModal={t('modalActivation.activationQuestion')}
          messageModal={t('modalActivation.insightsText')}
          textButton={t('modalActivation.buttonDone')}
          registerCompleted={registerCompleted}
        />
      )}
      <nav className={`menu-mobile ${className}`} style={getStylePosition()}>
        {mobileMenu.length && (
          <ul className="nav-items">
            {mobileMenu.map((item: menuItem, index: number) => {
              return <MenuItemRender item={item} t={t} key={index} />
            })}
          </ul>
        )}
      </nav>
    </>
  )
}
